import type { Question } from '@examflow/core';
import { samplePreferringPassageClusters } from '@examflow/core';
import {
  resolveDiagnosisPack,
  sampleDiagnosisPackQuestions,
  type ExamCode,
} from '@examflow/learning-space';
import { isUsableOpenEndedQuestion } from './openEndedWriting';

type SectionedQuestion = Question & {
  section?: string;
  taskNumber?: number;
  task?: number;
  topics?: string[];
  passageNumber?: number;
};

function questionTaskNumber(question: Question): number | null {
  const loose = question as SectionedQuestion;
  const raw = Number(loose.taskNumber ?? loose.task);
  return Number.isFinite(raw) && raw > 0 ? Math.trunc(raw) : null;
}

function questionHasTopic(question: Question, topicId: string): boolean {
  const topics = (question as SectionedQuestion).topics;
  if (!Array.isArray(topics) || topics.length === 0) return false;
  const needle = topicId.trim().toLowerCase();
  if (!needle) return false;
  return topics.some((t) => String(t).trim().toLowerCase() === needle);
}

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export function isUsableChoiceQuestion(question: Question): boolean {
  return (
    Array.isArray(question.options) &&
    question.options.length > 0 &&
    question.options.every((option) => option.id && option.content) &&
    !!question.correctAnswer
  );
}

export type PracticeSessionResponseMode = 'closed' | 'open_ended';

export type PracticePaperAssemblyInput = {
  questions: Question[];
  examType: string | null;
  sectionId: string | null;
  difficultyFilter: string | null;
  /**
   * Writing open-response task filter (Question.taskNumber / task).
   * Omit / null = any task.
   */
  taskNumberFilter?: number | null;
  /**
   * Writing content topic filter (Question.topics contains id).
   * Omit / null / empty = any topic.
   */
  topicIdFilter?: string | null;
  /**
   * Prefer this Question when assembling a 1-question writing session
   * (e.g. resume a local draft). Ignored if not in the filtered pool.
   */
  preferredQuestionId?: string | null;
  questionCount: number | 'all';
  /** When true, sample across skills via diagnosis pack quotas. */
  isDiagnosisPack: boolean;
};

export type PracticePaperAssemblyResult = {
  questions: Question[];
  /**
   * True when bank had only non-choice items and we did not assemble an open-ended
   * writing session (e.g. speaking-only inventory before oral runtime).
   */
  openResponseOnly: boolean;
  sessionResponseMode: PracticeSessionResponseMode;
  rawCount: number;
  usableChoiceCount: number;
  usableOpenEndedCount: number;
};

function prefersOpenEndedSession(sectionId: string | null, _openCount: number, _choiceCount: number) {
  // Writing Skill always uses open-ended Runtime — never fall back to choice chrome
  // when inventory is only cloze pollution or empty after filters.
  return sectionId === 'writing';
}

/**
 * Assemble the practice / diagnosis question list for Session Runtime.
 * Mock papers stay on core `buildPaper` — do not reuse this for mode=exam.
 */
export function assemblePracticeQuestionList(
  input: PracticePaperAssemblyInput
): PracticePaperAssemblyResult {
  const rawCount = input.questions.length;
  const choiceUsable = input.questions.filter(isUsableChoiceQuestion);
  const openUsable = input.questions.filter(isUsableOpenEndedQuestion);

  const useOpenEnded =
    !input.isDiagnosisPack &&
    prefersOpenEndedSession(input.sectionId, openUsable.length, choiceUsable.length);

  const sessionResponseMode: PracticeSessionResponseMode = useOpenEnded ? 'open_ended' : 'closed';
  const usable = useOpenEnded ? openUsable : choiceUsable;
  const openResponseOnly =
    rawCount > 0 && choiceUsable.length === 0 && openUsable.length > 0 && !useOpenEnded;

  let list = usable;

  if (input.difficultyFilter) {
    list = list.filter((q) => q.level === input.difficultyFilter);
  }

  if (useOpenEnded && input.taskNumberFilter != null && input.taskNumberFilter > 0) {
    list = list.filter((q) => questionTaskNumber(q) === input.taskNumberFilter);
  }

  const topicFilter = input.topicIdFilter?.trim().toLowerCase() || null;
  if (useOpenEnded && topicFilter) {
    list = list.filter((q) => questionHasTopic(q, topicFilter));
  }

  if (!input.isDiagnosisPack && input.sectionId && input.examType) {
    const targetSection = input.sectionId.trim().toLowerCase();
    const tagged = list.filter((q) => {
      const section = String((q as SectionedQuestion).section || '')
        .trim()
        .toLowerCase();
      return section === targetSection;
    });
    if (tagged.length > 0) {
      list = tagged;
    } else {
      // Access gate may include whole-exam inventories (bank.section null) whose
      // questions omit Skill tags. Treat untagged as belonging to the launched Skill
      // rather than stranding the learner on a "筛选为空" dead-end.
      const untagged = list.filter(
        (q) => !String((q as SectionedQuestion).section || '').trim()
      );
      list = untagged.length > 0 ? untagged : tagged;
    }
  }

  if (input.isDiagnosisPack && input.examType) {
    const availableSectionIds = Array.from(
      new Set(
        list
          .map((question) => (question as SectionedQuestion).section?.trim())
          .filter((sectionId): sectionId is string => Boolean(sectionId))
      )
    );
    const pack = resolveDiagnosisPack(input.examType as ExamCode, {
      readySectionIds: availableSectionIds,
    });
    return {
      questions: sampleDiagnosisPackQuestions(
        list.map((question) => ({
          ...question,
          section: (question as SectionedQuestion).section ?? undefined,
        })),
        pack
      ),
      openResponseOnly,
      sessionResponseMode: 'closed',
      rawCount,
      usableChoiceCount: choiceUsable.length,
      usableOpenEndedCount: openUsable.length,
    };
  }

  list = shuffleArray(list);

  const preferredId = input.preferredQuestionId?.trim() || null;
  if (preferredId) {
    const preferredIndex = list.findIndex((q) => q.id === preferredId);
    if (preferredIndex > 0) {
      const [preferred] = list.splice(preferredIndex, 1);
      list.unshift(preferred);
    }
  }

  if (input.questionCount !== 'all' && typeof input.questionCount === 'number' && input.questionCount > 0) {
    // 写作 resume（1 题 + preferred）保持置顶；其余按 passage 簇抽样
    if (preferredId && input.questionCount === 1) {
      list = list.slice(0, 1);
    } else {
      list = samplePreferringPassageClusters(list, input.questionCount, shuffleArray);
    }
  }

  return {
    questions: list,
    openResponseOnly,
    sessionResponseMode,
    rawCount,
    usableChoiceCount: choiceUsable.length,
    usableOpenEndedCount: openUsable.length,
  };
}
